import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { preAuthGet } from '../utils/preAuthApi';

interface IncidentBannerData {
  enabled: boolean;
  severity: 'info' | 'warning' | 'error';
  message: string;
  details: string;
}

const DEFAULT_BANNER: IncidentBannerData = {
  enabled: false,
  severity: 'warning',
  message: '',
  details: '',
};

const POLL_INTERVAL_MS = 60 * 1000; // 60 seconds — matches backend cache TTL
const VALID_SEVERITIES = new Set<IncidentBannerData['severity']>(['info', 'warning', 'error']);

export function useIncidentBanner() {
  const { pathname } = useLocation();
  const [bannerData, setBannerData] = useState<IncidentBannerData>(DEFAULT_BANNER);
  const [dismissed, setDismissed] = useState(false);
  const lastCheckRef = useRef(0);
  const dismissedMessageRef = useRef<string | null>(null);

  const fetchBanner = useCallback(async () => {
    const now = Date.now();
    if (now - lastCheckRef.current < POLL_INTERVAL_MS) {
      return;
    }
    lastCheckRef.current = now;

    try {
      const data = await preAuthGet('/api/incident-banner');
      const message = String(data.message || '');

      setBannerData({
        enabled: Boolean(data.enabled),
        severity: VALID_SEVERITIES.has(data.severity) ? data.severity : 'warning',
        message,
        details: String(data.details || ''),
      });

      // Reset dismissed state if the message changed (new incident)
      if (message !== dismissedMessageRef.current) {
        setDismissed(false);
      }
    } catch {
      // Silent failure — never crash the app for a banner fetch
    }
  }, []);

  // Check on route changes (also fires on mount)
  useEffect(() => {
    const id = setTimeout(() => { fetchBanner(); }, 0);
    return () => clearTimeout(id);
  }, [pathname, fetchBanner]);

  // Check on tab visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchBanner();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchBanner]);

  // Periodic polling — ensures banner appears even if user is idle on one page
  useEffect(() => {
    const id = setInterval(fetchBanner, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchBanner]);

  const dismiss = useCallback(() => {
    setDismissed(true);
    dismissedMessageRef.current = bannerData.message;
  }, [bannerData.message]);

  return { bannerData, dismissed, dismiss };
}
